import React, { useState } from 'react'
import { Page } from '../components/ui/Page'
import { Body } from '../components/layout/Body';

interface FAQItem {
    question: string
    answer: React.ReactNode
}

interface FAQSection {
    title: string
    intro?: React.ReactNode
    items: FAQItem[]
}

const faqSections: FAQSection[] = [
    {
        title: 'Hintergrund des Projekts',
        intro: (
            <div className="text-gray-600 mb-4 leading-relaxed">
                <p className="mb-3">
                    Aktuell ist die Beauftragung von Drohnenflügen mit hohen Nebenkosten verbunden. Diese Nebenkosten können durch
                    die Automatisierung von Prozessen, die bei den Befliegungen anfallen (z.&nbsp;B. Flugroutenplanung,
                    Datenübertragung, Datenauswertung), reduziert werden. Das Projekt Digital Sky Austria verfolgt dieses Ziel und
                    möchte damit zu einer niederschwelligen Verfügbarkeit von Drohnenflügen beitragen.
                </p>
                <p className="mb-3">
                    In einem Vorprojekt von Juni 2024 bis Juli 2025 wurde die technische, rechtliche und betriebswirtschaftliche
                    Machbarkeit von Digital Sky Austria festgestellt. In dieser Zeit wurde auch die Plattform Digital Sky Austria
                    entwickelt. Über die Plattform können Drohnenflüge online gebucht werden, und fliegen nach Freigabe eines
                    Drohnendienstleisters weitgehend automatisiert zu dem jeweiligen Anwendungsfall. Die Daten, die durch die
                    Drohnen erhoben werden, werden ausgewertet und an den Auftraggeber über die Plattform zurückgespielt.
                </p>
                <p className="mb-3">
                    Auf dieser Basis startete im November 2025 die Umsetzungsphase, um den Mehrwert von Digital Sky erlebbar zu
                    machen und die Plattform zur Nutzerreife weiterzuentwickeln. Dabei wird in der Stadt Klagenfurt ein
                    Pilotbetrieb aufgebaut – bestehend aus zwei stationären Drohnen-Hubs:
                </p>
                <ul className="list-disc pl-5 mb-3 text-[#666]">
                    <li>Standort A: Gewerbepark Viktring</li>
                    <li>Standort B: Kläranlage</li>
                </ul>
                <p>
                    Von diesen Drohnen-Hubs aus werden Befliegungen für einen begrenzten Nutzerkreis (z.&nbsp;B. die Stadtwerke
                    Klagenfurt) kostenfrei durchgeführt. Zudem werden von April 2026 bis September 2026 Veranstaltungen
                    organisiert, bei denen sich Gemeinden, Bundesländer und weitere interessierte Parteien (z.&nbsp;B.
                    Unternehmen) vom Nutzen der Plattform überzeugen können. Auch eine Veranstaltung im Beisein der Bürger ist bis
                    zum Projektende im Sommer 2026 geplant (der genaue Termin wird noch festgelegt).
                </p>
            </div>
        ),
        items: [],
    },
    {
        title: '1. Grundlagen und allgemeine Fragen',
        items: [
            {
                question: '1.1. Was ist die Drohnenplattform „Digital Sky Austria"?',
                answer: 'Digital Sky Austria ist eine Shared-Drohnenplattform, über die Drohnenbefliegungen gebucht, durchgeführt und ausgewertet werden können. Ziel ist es, Drohnentechnologie kostengünstig und effizient für die öffentliche Verwaltung, Unternehmen und Bürger zugänglich zu machen.',
            },
            {
                question: '1.2. Wie funktioniert die Drohnenplattform „Digital Sky Austria"?',
                answer: 'Nutzer bestellen Drohnenbefliegungen über die Plattform mit Anmeldung via ID-Austria, und geben Anwendungsfall, Ort und gewünschten Zeitraum für die Befliegung an. Der gebuchte Flug wird je nach Durchführbarkeit von einem Drohnendienstleister bestätigt oder abgelehnt. Die Drohnen starten dann automatisiert von stationären Hubs, die strategisch im Gemeindegebiet verteilt sind und fliegen selbstständig zum gebuchten Anwendungsfall. Die von der Drohne erhobenen Daten werden anschließend weitgehend automatisiert ausgewertet und den Nutzern über die Plattform wieder zur Verfügung gestellt.',
            },
            {
                question: '1.3. Wer kann diese Drohnenbefliegungen buchen?',
                answer: 'In der Endausbaustufe ist vorgesehen, dass die öffentliche Verwaltung, Privatpersonen oder Unternehmen Drohnenbefliegungen für ihre jeweiligen Anwendungsfälle buchen können. Dabei bekommt der Nutzer der Plattform nicht nur die Drohnenbefliegung selbst, sondern auch die jeweilige Auswertung zugespielt (z. B. ein 3D-Modell zu Vermessungszwecken). In der aktuellen Umsetzungsphase (April 2026 bis September 2026) ist die Buchung von Drohnenflügen ausschließlich für einen bereits definierten begrenzten Nutzerkreis vorgesehen, um die Plattform und den Drohnen-Hub zu erproben.',
            },
            {
                question: '1.4. Wofür können Drohnenbefliegungen gebucht werden?',
                answer: (
                    <>
                        <p className="mb-2">Digital Sky Austria dient verschiedenen Nutzergruppen und Anwendungsfällen:</p>
                        <ul className="list-disc pl-5 mb-3">
                            <li>
                                <strong>Privatpersonen</strong> sollen perspektivisch Drohnenbefliegungen für ihre eigenen Grundstücke
                                oder Liegenschaften buchen können, etwa zur Vermessung der eigenen Dachfläche oder für Bildaufnahmen
                                ihres Grundstückes.
                            </li>
                            <li>
                                <strong>Unternehmen</strong> können Drohnenbefliegungen im Rahmen ihrer betrieblichen Anwendungsfälle
                                nutzen, etwa für Inspektionen, Dokumentationen oder Planungszwecke. Die Nutzung erfolgt stets
                                zweckgebunden und im Interesse des jeweiligen Auftraggebers.
                            </li>
                            <li>
                                <strong>Öffentliche Stellen</strong> können Drohnenbefliegungen zur effizienteren Erfüllung ihrer
                                Tätigkeiten einsetzen, etwa für Infrastruktur-, Bau- oder Flächenmanagement. Der Einsatz von Drohnen
                                dient dabei als modernes Werkzeug zur Unterstützung bestehender Tätigkeiten und erfolgt aus Effizienz-
                                und Ressourcengründen.
                            </li>
                        </ul>
                        <p className="mb-2">Digital Sky Austria deckt dabei mehrere standardisierte Leistungskategorien ab, u. a.:</p>
                        <ul className="list-disc pl-5">
                            <li>Kartierungen und Vermessungen: z. B. Gebäude, Straßen, Baustellen</li>
                            <li>Inspektionen: z. B. Bauwerke, Denkmäler, Infrastruktur</li>
                            <li>Thermale Befliegungen: z. B. PV-Anlagen, Mikroklimata, Wärmeverluste</li>
                            <li>Luftbildaufnahmen: z. B. von Geländen, Gebäuden</li>
                        </ul>
                    </>
                ),
            },
            {
                question: '1.5. Was passiert, wenn ein Flug bereits gebucht wurde, die Wetterverhältnisse aber unpassend sind?',
                answer: 'Der Nutzer der Plattform kann lediglich einen Zeitraum für eine gewünschte Befliegung angeben. Der Drohnendienstleister versucht innerhalb dieses Zeitraums die Befliegung abzuwickeln. Sollte es aufgrund der Wetterverhältnisse nicht möglich sein, die Befliegung in diesem Zeitraum durchzuführen, tritt der Drohnendienstleister in Kontakt mit dem Auftraggeber. In einem solchen Fall wird der Flug verschoben oder zu einem späteren Zeitpunkt neu angesetzt.',
            },
        ],
    },
    {
        title: '2. Nutzen und Mehrwert des Projekts',
        items: [
            {
                question: '2.1. Wer profitiert von dem Projekt?',
                answer: 'Digital Sky Austria macht Drohnenflüge einfacher, günstiger und für viele überhaupt erst möglich. Durch gemeinsam genutzte Infrastruktur und automatisierte Abläufe müssen sich Nutzer nicht selbst um Technik, Piloten oder Genehmigungen kümmern. Das Projekt schafft die Grundlagen dafür, dass Gemeinden, Unternehmen und auch Bürger künftig moderne Drohnentechnologie unkompliziert und kostengünstig nutzen können.',
            },
            {
                question: '2.2. Welchen Nutzen hat die Gemeinde, wenn sie sich an Digital Sky Austria beteiligt?',
                answer: 'Indem eine Gemeinde Digital Sky Austria unterstützt (bspw. durch das Bereitstellen von Standorten oder die Vernetzung mit möglichen Bedarfsträgern), leistet sie einen aktiven Beitrag zu einer modernen, effizienten und zukunftsorientierten Verwaltung. Die Gemeinde erhält einfachen Zugang zu dem Drohnenservice für Aufgaben wie die Vermessung von Straßen und Gebäuden, die Kontrolle von Baustellen oder die frühzeitige Erkennung von Schäden an der Infrastruktur. Diese Leistungen werden ermöglicht, ohne dass die Gemeinde selbst in teure Technik oder laufende Betriebskosten investieren muss. Gleichzeitig positioniert sich die Gemeinde als innovationsfreundlicher Standort, welcher neue Technologien erprobt und damit Mehrwert für Verwaltung, Wirtschaft und Bevölkerung schafft.',
            },
            {
                question: '2.3. Welche Gemeinden können an Digital Sky Austria teilnehmen?',
                answer: 'Aktuell befindet sich das Projekt in der Umsetzungsphase und wird in Zusammenarbeit mit der Stadt Klagenfurt pilotiert. In dieser Projektphase sind keine Kooperationen mit weiteren Gemeinden geplant. In Zukunft ist vorgesehen, dass Digital Sky Austria auch in anderen Gemeinden Anwendung findet. Hierfür werden aktuell unterschiedliche Optionen geprüft, wie die Plattform nach der aktuellen Projektphase betrieben werden kann.',
            },
            {
                question: '2.4. Warum wurde Klagenfurt als Testgemeinde ausgewählt?',
                answer: 'Die Stadt Klagenfurt hat sich im Vorprojekt (Juni 2024 bis Juli 2025) als Stakeholder beim Projekt Digital Sky Austria gemeldet und im Zuge dessen Interesse geäußert an der gegenständlichen Projektphase beteiligt zu sein.',
            },
            {
                question: '2.5. Warum ist das Bundesministerium für Finanzen (BMF) der Initiator eines technischen Innovationsprojektes?',
                answer: 'Das BMF hat Digital Sky Austria initiiert, um neue digitale Infrastrukturen und innovative Technologien zu erproben, die langfristig zu effizienteren Abläufen, geringeren Kosten und besserer Nutzung öffentlicher Ressourcen beitragen können. Das Zugänglichmachen von Drohnenbefliegungen insbesondere für die öffentliche Verwaltung wird als systematischer Hebel gesehen, um Verwaltungskosten langfristig zu senken. Zudem kommt das BMF damit seinem gesetzlichen Auftrag gemäß Bundesministeriengesetz nach, wonach es für Technologietransfer und Sicherheitsforschung zuständig ist.',
            },
            {
                question: '2.6. Was passiert nach der aktuellen Phase?',
                answer: 'In der aktuellen Umsetzungsphase wird Digital Sky Austria im Pilotbetrieb erprobt. Parallel dazu werden unterschiedliche Optionen für einen möglichen zukünftigen Betrieb der Plattform ab September 2026 geprüft. Auf Basis der gewonnenen Erkenntnisse der Umsetzungsphase kann entschieden werden, ob und in welcher Form die Plattform Digital Sky Austria betrieben wird.',
            },
            {
                question: '2.7. Welche Kosten entstehen für die Bürger bzw. die Stadt in der aktuellen Phase?',
                answer: 'Die Dienstleistungen von Digital Sky Austria in der Umsetzungsphase werden kostenfrei zur Verfügung gestellt. Digital Sky Austria wurde vom BMF initiiert und finanziert, um den Mehrwert einer Shared Drohnenplattform zu demonstrieren und zur Verfügbarkeit von Drohnenbefliegungen beizutragen.',
            },
            {
                question: '2.8. Wie viel wird ein Drohnenflug in Zukunft in etwa kosten?',
                answer: (
                    <>
                        <p className="mb-2">
                            In der aktuellen Projektphase werden mögliche Betriebsmodelle für die Plattform evaluiert. Zum jetzigen
                            Zeitpunkt kann daher noch kein konkreter Preis genannt werden, zu dem Drohnenbefliegungen nach Abschluss
                            dieser Phase angeboten werden.
                        </p>
                        <p>
                            Gleichzeitig zeichnet sich ab, dass durch die Bündelung von Nebenkosten sowie durch die Automatisierung
                            der mit Drohnenbefliegungen verbundenen Prozesse künftig deutlich günstigere Angebote möglich sein werden
                            als derzeit. Aktuell liegen die Kosten pro Befliegung – abhängig vom jeweiligen Anwendungsfall und der
                            Einsatzdauer – zwischen 500 und 1.000 Euro.
                        </p>
                    </>
                ),
            },
        ],
    },
    {
        title: '3. Datenschutz & Privatsphäre',
        items: [
            {
                question: '3.1. Werde ich durch die Drohnen gefilmt oder überwacht?',
                answer: 'Nein. Digital Sky Austria dient nicht der Überwachung von Personen. Die Drohnen werden ausschließlich für klar definierte, sachliche Zwecke (z. B. Infrastruktur- oder Flächenaufnahmen, Vermessung) eingesetzt und nicht für Überwachungs- oder polizeiliche Aufgaben. Verantwortlich dafür, dass ein Objekt beflogen werden darf, ist der jeweilige Auftraggeber. Dieser kann mittels ID-Austria zweifelsfrei festgestellt werden.',
            },
            {
                question: '3.2. Wie wird meine Privatsphäre technisch geschützt?',
                answer: (
                    <>
                        <p className="mb-2">
                            Personen oder private Lebensbereiche stehen nicht im Fokus der Aufnahmen. Damit die Drohnen die
                            vorgesehenen Verwendungszwecke erfüllen können, fliegen sie in einer Höhe von 50 bis 100 Metern (bei
                            einer Bodenauflösung von ca. 1,35 bis 2,15 cm pro Pixel). Da für die Aufnahmen mit den Drohnen ein
                            schräger Flugwinkel erforderlich ist, sind Gesichter und Kfz-Kennzeichen auf den Aufnahmen nicht
                            erkennbar und somit auch nicht zuordenbar. Hinzu kommt, dass die Aufzeichnung der Daten, die im Anschluss
                            auch an den Auftraggeber übermittelt werden, erst ab der erreichten Flughöhe beginnt.
                        </p>
                        <p>
                            Zusätzlich sind die technischen Voraussetzungen für eine automatische Verpixelung von Gesichtern oder
                            Kfz-Kennzeichen gegeben. Sollten dennoch Unsicherheiten oder Bedenken auftreten, können diese jederzeit
                            über die Plattform gemeldet werden. Da alle Drohnenflüge ausschließlich nach Anmeldung über ID-Austria
                            beauftragt werden können, ist jederzeit nachvollziehbar, wer einen Flug veranlasst hat. Dadurch wird ein
                            transparenter und verantwortungsvoller Umgang mit Drohnenbefliegungen sichergestellt.
                        </p>
                    </>
                ),
            },
            {
                question: '3.3. Könnte das System für andere Zwecke (z. B. Polizeikontrolle) verwendet werden?',
                answer: 'Digital Sky Austria ist als zivile Plattform für klar definierte Anwendungsfälle konzipiert. Eine Nutzung für polizeiliche Überwachungs- oder Kontrollaufgaben ist nicht über die Plattform möglich, da relevante Anforderungen wie bspw. Live-Übertragung nicht eingebunden sind.',
            },
            {
                question: '3.4. Kann mein Nachbar einen Flug buchen und mich ausspionieren?',
                answer: 'Nein. Drohnenflüge können nicht beliebig und anonym durchgeführt werden. Jede Befliegung muss über die Plattform gebucht werden und ist an einen klar definierten Zweck gebunden (bspw. Vermessungen). Der Auftraggeber wird dabei eindeutig identifiziert (z. B. über ID-Austria).',
            },
            {
                question: '3.5. Was passiert mit den erhobenen Daten?',
                answer: 'Die erhobenen Daten werden ausschließlich dem jeweiligen Auftraggeber der Befliegung zur Verfügung gestellt. Eine Weitergabe an Dritte erfolgt nicht. Die Daten werden nur öffentlich zugänglich gemacht, wenn der Auftraggeber dies ausdrücklich über die Plattform freigibt.',
            },
            {
                question: '3.6. Wer hat Zugriff auf die Daten?',
                answer: 'Zugriff auf die Daten haben ausschließlich der beauftragte Drohnendienstleister zu Verarbeitungszwecken sowie der Auftraggeber der Befliegung, sofern der Auftraggeber die Daten nicht ausdrücklich über die Plattform für die Öffentlichkeit freigibt.',
            },
            {
                question: '3.7. Wo werden die erhobenen Daten gespeichert?',
                answer: (
                    <>
                        <p className="mb-2">Die bei den Drohnenbefliegungen erhobenen Daten werden an zwei Stellen gespeichert.</p>
                        <p className="mb-2">
                            Zum einen liegen sie auf den Servern von DJI, dem Hersteller der eingesetzten Drohnen. DJI ist jener
                            Hersteller, dessen Systeme in der aktuellen Projektphase auch von den Drohnendienstleistern verwendet
                            werden.
                        </p>
                        <p className="mb-2">
                            Zum anderen werden die Daten zur weiteren Verarbeitung bzw. Auswertung auf den Servern des technischen
                            Dienstleisters des Projekts gespeichert. Dieser hat die Plattform entwickelt; der Serverstandort befindet
                            sich in Frankfurt, Deutschland. Die Speicherung der Daten erfolgt dabei nach aktuell geltendem EU-Recht.
                        </p>
                        <p>
                            Die Daten werden von den Servern von DJI gelöscht, sobald sie auf die Server von Digital Sky Austria
                            übertragen wurden.
                        </p>
                    </>
                ),
            },
            {
                question: '3.8. Wie lange werden die erhobenen Daten gespeichert?',
                answer: 'Im Anschluss an die operative Verarbeitung werden die Daten aufgrund gesetzlicher Vorgaben noch sieben Jahre aufbewahrt. Diese Frist ergibt sich aus der gesetzlichen Aufbewahrungspflicht geschäftsbezogener Daten gemäß § 190 UGB. Nach Ablauf dieser Frist werden die Daten endgültig gelöscht.',
            },
            {
                question: '3.9. Kann ich Bedenken oder Beschwerden melden?',
                answer: 'Ja. Auf der Plattform Digital Sky Austria wird im Zuge der Umsetzungsphase bis zum Start der Befliegungen im April 2026 ein Meldebutton integriert, über den Anliegen oder Bedenken zu möglichen Urheberrechtsverletzungen direkt eingebracht werden können.',
            },
        ],
    },
    {
        title: '4. Sicherheit & technische Zuverlässigkeit',
        items: [
            {
                question: '4.1. Wie sicher sind die Drohnenflüge?',
                answer: 'Die eingesetzten Drohnen verfügen über mehrere Sicherheitsmechanismen, darunter automatische Hinderniserkennung, Fallschirmsysteme, Notlandefunktionen sowie eine automatische Rückkehr zum Drohnen-Hub bei Störungen.',
            },
            {
                question: '4.2. Werden die beauftragten Drohnenflüge vor der Durchführung individuell geprüft?',
                answer: 'Alle in der Plattform beauftragten Flüge werden vor der Durchführung nochmals durch einen zertifizierten Drohnenpiloten in einem zweistufigen Prozess geprüft in Hinblick auf die Durchführbarkeit des Anwendungsfalles und die mögliche Flugroute. Diese Freigabe erfolgt zunächst in der Plattform Digital Sky Austria. Hierbei wird der Anwendungsfall auf Durchführbarkeit geprüft. Im Anschluss erfolgt die zweite Freigabe in Flight Hub (Flugmanagement-Software), wobei die Flugroute kontrolliert und bestätigt wird.',
            },
            {
                question: '4.3. Was passiert bei technischen Problemen oder Störungen?',
                answer: 'Im Störungsfall (z. B. Signalverlust) wechselt die Drohne in einen sicheren Langsamflugmodus und landet kontrolliert oder kehrt in sicherer Höhe (bis zu 100 Meter) zum Drohnen-Hub zurück. Sollte eine Rückkehr zum Drohnen-Hub nicht möglich sein und auch eine kontrollierte Landung scheitern, verfügen die eingesetzten Drohnen über integrierte Fallschirme, die den Sturz kontrolliert abbremsen. Sollte dennoch ein Schaden entstehen, haftet der jeweils beauftragte Drohnendienstleister, der hierfür entsprechend versichert ist.',
            },
            {
                question: '4.4. Wie wird verhindert, dass Drohnen mit Vögeln, hohen Bäumen etc. kollidieren?',
                answer: (
                    <>
                        <p className="mb-2">
                            Die eingesetzten Drohnen verfügen über mehrere Sicherheitsmechanismen zur Vermeidung von Kollisionen.
                            Grundsätzlich werden bereits in der Flugplanung geeignete Flughöhen und ausreichende Sicherheitsabstände
                            definiert, um bekannte Hindernisse zu umgehen.
                        </p>
                        <p>
                            In niedrigen Flughöhen bewegen sich die Drohnen bewusst mit reduzierter Geschwindigkeit, sodass die
                            integrierten Sensoren Hindernisse zuverlässig erkennen können. Die Drohnen sind zudem in der Lage, ihren
                            Flugpfad automatisch an erkannte Hindernisse anzupassen. Vögel stellen in der Praxis kein relevantes
                            Risiko dar, da sie große Fluggeräte in der Regel meiden.
                        </p>
                    </>
                ),
            },
            {
                question: '4.5. Werden die Wetter- und Windverhältnisse laufend überwacht und berücksichtigt?',
                answer: 'Die Drohnen-Hubs sind mit einem integrierten Windmesser und einem Regensensor ausgestattet, die die Wetterverhältnisse kontinuierlich überwachen. Ein Start ist nur innerhalb klar definierter Betriebsgrenzen zulässig. Die festgelegte Windgrenze liegt bei 12 m/s, was etwa 40 km/h entspricht. Werden diese Grenzwerte überschritten, startet die Drohne nicht bzw. wird ein laufender Betrieb entsprechend unterbrochen.',
            },
            {
                question: '4.6. Gibt es eine Art Notfallprotokoll?',
                answer: (
                    <>
                        <p className="mb-2">
                            Ja. Für den Betrieb der Drohnen besteht ein klar definiertes Sicherheits- und Notfallkonzept. Dieses
                            regelt das Vorgehen bei technischen Störungen, unerwarteten Wetteränderungen oder sonstigen Abweichungen
                            vom geplanten Flug.
                        </p>
                        <p>
                            Im Störungsfall reagiert die Drohne automatisch, etwa durch Reduktion der Geschwindigkeit, kontrollierte
                            Landung oder Rückkehr zum Drohnen-Hub. Parallel dazu überwacht ein Drohnendienstleister den Flug und kann
                            bei Bedarf manuell eingreifen.
                        </p>
                    </>
                ),
            },
            {
                question: '4.7. Wer überwacht die Flüge?',
                answer: (
                    <>
                        <p className="mb-2">
                            In der aktuellen Phase werden alle Flüge durch einen zertifizierten Drohnendienstleister beaufsichtigt.
                            Während der gesamten Durchführung der Befliegungen hat ein Drohnenpilot kontinuierlich den Überblick über
                            das aktuelle Fluggeschehen. Der Flug wird über einen Remote-Controller überwacht, über den der
                            Drohnenpilot im Notfall jederzeit eingreifen und die manuelle Steuerung übernehmen kann.
                        </p>
                        <p className="mb-2">
                            Für die manuelle Steuerung der Drohne ist keine Sichtverbindung zur Drohne notwendig, da dies durch die
                            vorhandenen Kameras gegeben ist. Diese Kameras auf der Drohne übertragen Bilder zum aktuellen
                            Fluggeschehen aus mehreren Perspektiven zum einen auf den Remote-Controller und zum anderen auch auf
                            Flight-Hub (eine Online-Anwendung).
                        </p>
                        <p>
                            Ein vollständig unbeaufsichtigter Betrieb ist aktuell in der Umsetzungsphase von April 2026 bis September
                            2026 nicht vorgesehen.
                        </p>
                    </>
                ),
            },
            {
                question: '4.8. Sind die Drohnenflüge rechtlich genehmigt?',
                answer: 'Ja. Alle Drohnenflüge im Rahmen von Digital Sky Austria erfolgen gemäß der geltenden EU-Drohnenverordnung und ausschließlich innerhalb eines von der Austro Control genehmigten Luftraums. Die Drohnen sind registriert, der Betrieb ist genehmigt und es wurden umfassende Risikoanalysen (Luft- und Bodenrisiken) durchgeführt. Die Drohnen fliegen ausschließlich innerhalb des von der Austro Control genehmigten Radius von 1 km um den Drohnen-Hub. Die Flugrouten werden im Vorfeld festgelegt und technisch so abgesichert, dass die Drohnen nur innerhalb des genehmigten Flugradius und der zulässigen Flughöhen operieren.',
            },
            {
                question: '4.9. Wer ist für die Wartung der Drohnen und Drohnen-Hubs verantwortlich?',
                answer: 'Die Wartung der Drohnen und Drohnen-Hubs erfolgt durch den beauftragten Drohnendienstleister im Rahmen regelmäßiger Wartungs- und Inspektionstermine (mindestens einmal pro Monat). Zudem hat der Drohnendienstleister Zugriff auf das Online-Interface der Drohnen-Hubs und kann dort jederzeit auf Fehlermeldungen reagieren.',
            },
            {
                question: '4.10. Was passiert bei Schäden?',
                answer: 'Sollte es im Zusammenhang mit einem Drohnenflug zu einem Schaden kommen, haftet der durchführende Drohnendienstleister. Für diesen Zweck bestehen entsprechende Versicherungen. Die Drohnen werden ausschließlich von zertifizierten Piloten betrieben und alle Flüge erfolgen im Rahmen der geltenden rechtlichen Vorgaben, um Risiken bestmöglich zu minimieren.',
            },
        ],
    },
    {
        title: '5. Lärm- und Sichtbelastung',
        items: [
            {
                question: '5.1. Wie laut sind die Drohnen?',
                answer: 'Die Lärmbelastung ist gering. Beim Start bzw. der Landung der Drohne ist von ca. 70 dB auszugehen, vergleichbar mit der Lautstärke des Straßenverkehrs. Im Flug nimmt der Geräuschpegel mit steigender Entfernung deutlich ab und ist bei einer Höhe von 30 Metern vergleichbar mit einem Ventilatoren-Geräusch. Die Drohnen fliegen in einer Höhe von bis zu 100 Metern und sind damit akustisch kaum wahrnehmbar.',
            },
            {
                question: '5.2. Sind die Drohnen ständig sichtbar?',
                answer: 'Nein. Aufgrund der Flughöhe und der geringen Größe (ca. 38 × 42 × 21 cm) sind die Drohnen nur kurzzeitig und eingeschränkt sichtbar.',
            },
            {
                question: '5.3. Werden Natur- oder Schutzgebiete überflogen?',
                answer: 'Nein. Durch die Positionierung der Drohnen-Hubs in Klagenfurt wird sichergestellt, dass sensible Habitate wie Brutgebiete, Naturschutzflächen oder Wildtierkorridore nicht überflogen werden. Auch für zukünftige Standorte in Österreich stellt dies bei der Standortwahl der Drohnen-Hubs ein wesentliches Kriterium dar.',
            },
            {
                question: '5.4. Wird auf Anrainer Rücksicht genommen?',
                answer: 'Ja. Bereits bei der Planung werden mögliche Auswirkungen auf Anrainer berücksichtigt. Dazu zählt insbesondere die sorgfältige Auswahl und Platzierung der Drohnen-Hubs, um Beeinträchtigungen so gering wie möglich zu halten. So wurden beispielsweise in der aktuellen Projektphase gezielt Dächer von Industriegebäuden ausgewählt – und keine Wohngebäude.',
            },
            {
                question: '5.5. Kann ich verhindern, dass mein Grundstück beflogen wird (Opt-out-Option)?',
                answer: 'Aktuell besteht keine Opt-out-Option für Grundstücke. Allerdings beginnt die Datenaufnahme der Drohnen erst, sobald diese ihren Einsatzort erreicht haben. Sollte also im Zuge des An- oder Abflugs ein Grundstück überflogen werden, so werden diese Daten nicht gespeichert.',
            },
            {
                question: '5.6. Über welche Flächen fliegen die Drohnen auf dem Weg zum Einsatzgebiet, und wie wird mit sensiblen Bereichen umgegangen?',
                answer: (
                    <>
                        <p className="mb-2">
                            Im Sinne der Ressourcenoptimierung fliegen die Drohnen auf dem kürzesten Weg zu ihrem Einsatzort. Ein
                            vollständiges Vermeiden privater Grundstücke ist insbesondere in dicht bebauten Gebieten nicht möglich.
                            Die Drohnen bewegen sich jedoch in sicherer Höhe und eine Datenaufnahme beginnt ausschließlich über dem
                            gebuchten Einsatzgebiet.
                        </p>
                        <p>
                            Bei der Planung von Drohnen-Hubs und Flugrouten wird zudem gezielt darauf geachtet, sensible Bereiche wie
                            Schulen, Kindergärten oder Spielplätze möglichst zu vermeiden. Die Standorte der Drohnen-Hubs werden
                            entsprechend gewählt, und Flugrouten können so geplant werden, dass sensible Bereiche – sofern
                            erforderlich – nur außerhalb der Nutzungszeiten (z. B. in schulfreien Zeiten) überflogen werden.
                        </p>
                    </>
                ),
            },
            {
                question: '5.7. Stellen die Drohnenflüge ein Risiko für Kinder dar?',
                answer: 'Die Flüge erfolgen in einer Höhe, die für Kinder nicht erreichbar ist und in der die Drohnen im Alltag nur eingeschränkt wahrnehmbar sind. Kinder oder private Lebensbereiche stehen nicht im Fokus der Aufnahmen. Sollte dennoch der Eindruck entstehen, dass sensible Bereiche betroffen sind, können diese Fälle über die Plattform gemeldet werden.',
            },
        ],
    },
    {
        title: '6. Weitere mögliche Bedenken von Seiten der Bevölkerung',
        items: [
            {
                question: '„Ich will keine Drohne über meinem Grundstück."',
                answer: 'Dieses Anliegen ist nachvollziehbar. Drohnenflüge erfolgen im Rahmen von Digital Sky Austria nicht flächendeckend und immer mit konkretem Zweck. Jede Befliegung ist an einen klar definierten Anwendungsfall gebunden und erfolgt nur dann, wenn sie für diesen Zweck erforderlich ist. Bei der Flugplanung wird darauf geachtet, den Überflug über private Grundstücke so gering wie möglich zu halten, darüber hinaus beginnt die Datenaufnahme erst über dem eigentlichen Einsatzort.',
            },
            {
                question: '„Das Projekt dient dazu, die Bevölkerung auszuspionieren."',
                answer: 'Digital Sky Austria ist kein Überwachungsprojekt. Die Plattform dient ausschließlich sachlichen, klar definierten Anwendungsfällen wie Vermessung, Infrastruktur- oder Flächenerfassung. Es gibt keine dauerhaften oder automatisierten Überwachungsflüge. Jede Befliegung wird einzeln beauftragt, genehmigt und durchgeführt. Personen oder private Lebensbereiche sind nicht Ziel der Datenerhebung, und es findet keine Beobachtung des Alltags statt.',
            },
            {
                question: '„Das ist nur der Anfang – bald werden wir komplett überwacht."',
                answer: 'Digital Sky Austria ist zeitlich, räumlich und inhaltlich klar begrenzt. In der aktuellen Umsetzungsphase handelt es sich um einen Pilotbetrieb mit einem eingeschränkten Nutzerkreis und klar definierten Einsatzszenarien (bspw. die Vermessung des Geländes der Kläranlage Klagenfurt).',
            },
            {
                question: '„Warum wurde die Bevölkerung vorher nicht befragt?"',
                answer: (
                    <>
                        <p className="mb-2">
                            In der vorhergehenden Projektphase von Juni 2024 bis Juli 2025 wurden verschiedene Stakeholder aus
                            unterschiedlichen österreichischen Gemeinden (u. a. Hochfilzen, Amlach, Linz, Kufstein, Klagenfurt und
                            Villach) befragt. Diese haben sowohl Bedarf an Drohnenbefliegungen gemeldet als auch Interesse am
                            Projekt Digital Sky Austria geäußert.
                        </p>
                        <p>
                            Digital Sky Austria befindet sich derzeit in einer Pilotphase, in der technische und organisatorische
                            Aspekte erprobt werden (z. B. erforderliche Datenformate, mögliche Nutzergruppen und die Anwendbarkeit
                            der Plattform). Ziel dieser Phase ist es, Erfahrungen zu sammeln und Rückmeldungen zu berücksichtigen,
                            weshalb die Plattform für einen begrenzten Nutzerkreis zur Verfügung gestellt wird.
                        </p>
                    </>
                ),
            },
            {
                question: '„Ein Projekt, das viel kostet und am Ende nichts bringt. Die Stadt hat ja ohnehin kein Geld."',
                answer: 'In der aktuellen Umsetzungsphase entstehen der Stadt keine Kosten. Das Projekt wird vom BMF finanziert, um zu prüfen, ob durch gemeinsame Infrastruktur und Automatisierung künftig effizienter gearbeitet werden kann. Ziel ist es, langfristig Kosten zu senken, etwa durch weniger externe Beauftragungen, schnellere Abläufe und bessere Datengrundlagen.',
            },
        ],
    },
]

function FAQAccordionItem({ question, answer }: FAQItem) {
    const [open, setOpen] = useState(false)

    return (
        <div className="border-b border-gray-200 last:border-0">
            <button
                type="button"
                className="w-full flex justify-between items-center py-4 text-left gap-4 hover:text-[#E1320F] transition-colors"
                onClick={() => setOpen(o => !o)}
            >
                <span className="font-medium text-sm">{question}</span>
                <span className="text-lg shrink-0 text-gray-400">{open ? '−' : '+'}</span>
            </button>
            {open && (
                <div className="pb-4 text-sm text-gray-600 leading-relaxed">
                    {typeof answer === 'string' ? <p>{answer}</p> : answer}
                </div>
            )}
        </div>
    )
}

export default function FAQPage() {
    return (
        <div className="max-w-[1200px] mx-auto">
            <Page>
                <Body>
                    <h1 className='text-2xl mt-16 mb-8 font-bold'>FAQ – Digital Sky Austria
                    </h1>

                    {faqSections.map(section => (
                        <div key={section.title} className="w-full mb-8">
                            <h2 className="text-lg font-semibold mb-3 text-2xl">{section.title}</h2>
                            {section.intro}
                            {section.items.length > 0 && (
                                <div className="border border-gray-200 rounded-xl px-4">
                                    {section.items.map(item => (
                                        <FAQAccordionItem key={item.question} {...item} />
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </Body>
            </Page>
        </div>
    )
}
